import { makeAutoObservable } from 'mobx';

import { deleteLastSearchRequest, deleteSavedFilterRequest, getFiltersTabsData } from 'api/tabs';
import { TFilterInfo } from 'api/userFilters/types';
import { SubscribedFilter } from 'store/Subscriptions/SubscribedFilter';
import { snakeToCamelObj } from 'utils/objects';

import { RootStore } from '../RootStore';
import { TrucksDataStore } from './TrucksTabDataStore';

export type SavedFilter = TFilterInfo & {
  id: string;
  boardsAvailability?: number;
};

export type TabsData = {
  savedFilters: SavedFilter[];
  lastSearches: SavedFilter[];
  truckSearches?: SavedFilter[];
};

export enum ViewFilterTypes {
  all = 'all',
  personal = 'personal',
  collective = 'collective',
}

class TabsDataStore {
  root;
  data: TabsData | null;
  trucks: TrucksDataStore;
  selectedFilter: SavedFilter | null;
  viewFilterType: ViewFilterTypes = ViewFilterTypes.all;

  constructor(root: RootStore) {
    this.root = root;
    this.trucks = new TrucksDataStore(root);
    this.data = {
      savedFilters: [],
      lastSearches: [],
      truckSearches: [],
    };
    this.selectedFilter = null;

    makeAutoObservable(this);
  }

  get savedFilters() {
    return this.data?.savedFilters.map(savedFilter => new SubscribedFilter(this.root, savedFilter));
  }

  get savedFiltersById() {
    const data = this.savedFilters?.reduce<{ [key: string]: SubscribedFilter }>((acc, subscribedFilter) => {
      acc[subscribedFilter.data.id] = subscribedFilter;

      return acc;
    }, {});

    return data;
  }

  get savedFiltersData() {
    return this.data?.savedFilters
      .map(savedFilter => new SubscribedFilter(this.root, savedFilter).data)
      .sort((a, b) => Number(b.instance?.sortingScore) - Number(a.instance?.sortingScore));
  }

  get filtersView() {
    return this[this.viewFilterType];
  }

  get all() {
    return this.savedFiltersData;
  }

  get personal() {
    return this.all?.filter(filter => !filter.isCollective);
  }

  get collective() {
    return this.all?.filter(filter => filter.isCollective);
  }

  setViewFilterType = (viewFilterType: ViewFilterTypes) => {
    this.viewFilterType = viewFilterType;
  };

  addSavedFilter = (addedFilter: SavedFilter) => {
    this.data?.savedFilters.push(addedFilter);
  };

  updateSavedFilter = (updatedFilter: Partial<SavedFilter>) => {
    if (!this.data?.savedFilters) return;

    this.data.savedFilters = this.data.savedFilters.map(filter => {
      if (filter.id === updatedFilter.id) return { ...filter, ...updatedFilter };

      return filter;
    });
  };

  deleteSavedFilterById = (filterId: string) => {
    if (!this.data?.savedFilters) return;

    this.data.savedFilters = this.data.savedFilters.filter(filter => filter.id !== filterId);
  };

  deleteSubscriptionByFilterId = (filterId: string) => {
    this.updateSavedFilter({ id: filterId, subscription: undefined });
  };

  setData = (data: TabsData) => {
    this.data = data;
  };

  fetchTabsData = async () => {
    this.root.app.setLoading('tabsData', true);
    try {
      const { data } = await getFiltersTabsData();

      this.setData({
        savedFilters: snakeToCamelObj(data.saved_filters),
        lastSearches: snakeToCamelObj(data.last_searches),
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.root.app.setLoading('tabsData', false);
    }
  };

  deleteSavedFilter = async (id: string) => {
    this.root.app.setLoading('deleteSavedFilter', true);
    try {
      const { data } = await deleteSavedFilterRequest(id);

      if (data.success === true) {
        this.deleteSavedFilterById(id);
      }
    } catch (error: any) {
      const { response } = error;

      if (response?.status === 403) {
        return this.root.errors.setRetrievable({
          name: 'deleteSavedFilterError',
          message: response.data.message,
          type: 'warning',
        });
      }

      this.root.errors.setRetrievable({
        name: 'deleteSavedFilterError',
        message: this.root.app.i18n.errors.deleteSavedFilterError,
      });
      console.error(error);
    } finally {
      this.root.app.setLoading('deleteSavedFilter', false);
    }
  };

  deleteLastSearch = async (id: string) => {
    this.root.app.setLoading('deleteLastSearchRequest', true);
    try {
      const { status } = await deleteLastSearchRequest(id);

      if (status === 200) {
        this.fetchTabsData();
      }
    } catch (error) {
      this.root.errors.setRetrievable({
        name: 'deleteLastSearchRequestError',
        message: this.root.app.i18n.errors.deleteLastSearchRequestError,
      });
      console.error(error);
    } finally {
      this.root.app.setLoading('deleteLastSearchRequest', false);
    }
  };

  appendLastSearch = (lastSearch: SavedFilter) => {
    const alreadySearched = this.data?.lastSearches.filter(search => search.id === lastSearch.id);

    if (alreadySearched?.length) return;

    this.data?.lastSearches.unshift(lastSearch);
    if (this.data?.lastSearches) {
      this.data.lastSearches = this.data?.lastSearches.slice(0, 15);
    }
  };

  selectFilter = (filter: SavedFilter) => {
    this.selectedFilter = filter;
  };

  get subscribedToSelectedFilter() {
    return Boolean(this.selectedFilter?.subscription);
  }
}

export { TabsDataStore };
